import { usePermissions } from '@ltvco/refresh-lib/ctx';
import { Stack } from '@ltvco/refresh-lib/theme';

import { FreePlanDisclaimer } from 'components/ChatAI/components/FreePlanDisclaimer';

import { RetrySearchInstruction } from '../../components/RetrySearchInstruction';
import { EnterVehicleDataInstruction } from '../EnterVehicleDataInstruction/EnterVehicleDataInstruction';
import { ValueForecasterResultCard } from '../ValueForecasterResultCard/ValueForecasterResultCard';

import type { VehicleForecast } from '@ltvco/refresh-lib/api';

type ValueForecasterContentProps = {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  firstValueForecastData: VehicleForecast | null | undefined;
  marketValue: number;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  hasError?: boolean;
  onNewForecast: () => void;
  isLoading: boolean;
  onTooltipOpenChange?: (isOpen: boolean) => void;
};

/**
 * ValueForecasterContent - Main content container for Value Forecaster
 * Shows instruction prompt and first forecast result if available
 * @component
 */
export const ValueForecasterContent = ({
  onSendMessage,
  firstValueForecastData,
  marketValue,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
  hasError = false,
  onNewForecast,
  isLoading,
  onTooltipOpenChange,
}: ValueForecasterContentProps) => {
  const { isFreePlan } = usePermissions();
  const hasStarted = Boolean(firstValueForecastData) || hasError;
  const showResultCard = firstValueForecastData && !hasError;

  return (
    <Stack alignItems="center" gap={1} width="100%">
      <EnterVehicleDataInstruction
        onSendMessage={onSendMessage}
        hasConversationStarted={hasStarted}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={onResetComplete}
        onProcessingStateChange={onProcessingStateChange}
        isLoading={isLoading}
      />
      {showResultCard && (
        <>
          <ValueForecasterResultCard
            forecastData={firstValueForecastData}
            marketValue={marketValue}
            onTooltipOpenChange={onTooltipOpenChange}
          />
          {isFreePlan && <FreePlanDisclaimer entityType="value_forecaster" />}
          <RetrySearchInstruction onRetrySearch={onNewForecast} />
        </>
      )}
    </Stack>
  );
};
